/**
 * Billable rate resolution.
 *
 * The effective rate for a unit of labor is resolved from the most specific input
 * available, in priority order:
 *
 *   1. `override`          — explicit per-time-entry rate override
 *   2. `phase`             — negotiated project-phase rate for the service category
 *                            (when the entry is attached to a project phase)
 *   3. `project`           — negotiated project rate for the service category
 *                            (when the entry is attached to a project)
 *   4. `agreement`         — negotiated agreement rate for the service category
 *                            (when the entry is attached to an agreement)
 *   5. `service_category`  — the service category's default rate
 *
 * A time entry is attached to either a project or an agreement (never both), so at
 * most one of `phaseRate` / `projectRate` / `agreementRate` is supplied for any
 * resolution.
 *
 * Rates are decimals serialized as strings end-to-end (see docs/conventions.md);
 * resolution selects a value and never performs floating-point math.
 */
export const RATE_SOURCES = [
    'override',
    'phase',
    'project',
    'agreement',
    'service_category',
    'none',
];
/** Short, human-readable label for each rate source (UI display). */
export const RATE_SOURCE_LABELS = {
    override: 'Entry override rate',
    phase: 'Phase override rate',
    project: 'Project rate',
    agreement: 'Agreement rate',
    service_category: 'Service category default rate',
    none: 'No rate configured',
};
/** One-line explanation of where a resolved rate came from (for hover tooltips). */
export const RATE_SOURCE_EXPLANATIONS = {
    override: 'This rate is the entry-level override set directly on this time entry.',
    phase: "This rate comes from the project phase's override rate.",
    project: "This rate comes from the project's rate for this service category.",
    agreement: "This rate comes from the agreement's rate for this service category.",
    service_category: "This rate is the service category's default rate.",
    none: 'No rate source applied, so the rate defaulted to 0.',
};
function hasValue(v) {
    return v !== null && v !== undefined && v !== '';
}
/**
 * Resolves the billable rate from the available inputs by priority. Returns a
 * `none` source with rate "0" when no input provides a rate.
 */
export function resolveRate(input) {
    if (hasValue(input.rateOverride)) {
        return { rate: input.rateOverride, source: 'override' };
    }
    if (hasValue(input.phaseRate)) {
        return { rate: input.phaseRate, source: 'phase' };
    }
    if (hasValue(input.projectRate)) {
        return { rate: input.projectRate, source: 'project' };
    }
    if (hasValue(input.agreementRate)) {
        return { rate: input.agreementRate, source: 'agreement' };
    }
    if (hasValue(input.serviceCategoryDefaultRate)) {
        return { rate: input.serviceCategoryDefaultRate, source: 'service_category' };
    }
    return { rate: '0', source: 'none' };
}
//# sourceMappingURL=rate.js.map